import { AxiosError } from 'axios';
import axiosInstance from '../utils/axios';

/**
 * The three student-facing surfaces that had a working API and no interface:
 * the diagnostic teaser (#08), the referral panel (#09) and the store (#10).
 *
 * They share this file because they share one rule: a `402` is a product state,
 * not a failure. Collapsing it to null — the pattern used elsewhere in this app —
 * would erase the difference between "you have not bought this" and "something
 * broke", which is exactly the distinction the paywall is made of. Each call
 * returns a discriminated result, matching sessionReviewService.
 */

const isAxios = (e: unknown): e is AxiosError => (e as AxiosError)?.isAxiosError === true;

const classify = (error: unknown): 'locked' | 'unauthorized' | 'error' => {
  if (!isAxios(error)) return 'error';
  const status = error.response?.status;
  if (status === 402) return 'locked';
  if (status === 401 || status === 403) return 'unauthorized';
  return 'error';
};

/** These three endpoints are free by design, so a 402 from them is a server bug, not a paywall. */
const nonPaywalled = (error: unknown): 'unauthorized' | 'error' => {
  const state = classify(error);
  return state === 'locked' ? 'error' : state;
};

// ── #08 diagnostic teaser ────────────────────────────────────────────────────

export interface LockedPanel {
  key: string;
  title: string;
  description: string;
  /** The SKU this panel unlocks — carried to checkout so the CTA buys the right thing. */
  sku: string;
  locked: boolean;
}

export interface SessionTeaser {
  sessionId: string;
  packageId: string;
  score: number;
  totalScore: number;
  percentage: number;
  questionsTotal: number;
  questionsAttempted: number;
  questionsCorrect: number;
  /** The skill the student did worst at — the hook the funnel hangs on. */
  weakestSkill?: string;
  weakestSkillAccuracyPct?: number;
  hook?: string;
  lockedPanels: LockedPanel[];
}

export type TeaserResult =
  | { state: 'ready'; teaser: SessionTeaser }
  | { state: 'unauthorized' | 'error'; message?: string };

export const fetchSessionTeaser = async (sessionId: string): Promise<TeaserResult> => {
  try {
    const response = await axiosInstance.get(`/sessions/${sessionId}/teaser`);
    const data = response.data?.data ?? response.data;
    if (!data) return { state: 'error', message: 'The result summary was empty.' };
    return { state: 'ready', teaser: { ...data, lockedPanels: data.lockedPanels ?? [] } };
  } catch (error) {
    return { state: nonPaywalled(error) };
  }
};

// ── #09 referrals ────────────────────────────────────────────────────────────

export interface Referral {
  code: string;
  rewardSku: string;
  redemptions: number;
  active: boolean;
}

export type ReferralResult =
  | { state: 'ready'; referral: Referral }
  | { state: 'unauthorized' | 'error'; message?: string };

/** The caller's own code. The server issues one on first read, so this never returns empty. */
export const fetchMyReferral = async (): Promise<ReferralResult> => {
  try {
    const response = await axiosInstance.get('/referrals/me');
    const data = response.data?.data ?? response.data;
    if (!data?.code) return { state: 'error', message: 'No referral code was returned.' };
    return { state: 'ready', referral: data };
  } catch (error) {
    return { state: nonPaywalled(error) };
  }
};

// ── #10 store ────────────────────────────────────────────────────────────────

export interface CatalogItem {
  packageId: string;
  name: string;
  grade: string[] | string;
  subject: string[] | string;
  sku: string;
  /** Integer paise. Never treat as a float; divide by 100 for display only. */
  amountPaise: number;
  currency: string;
  questionCount: number;
  owned: boolean;
}

export type CatalogResult =
  | { state: 'ready'; items: CatalogItem[] }
  | { state: 'unauthorized' | 'error'; message?: string };

export const fetchStoreCatalog = async (): Promise<CatalogResult> => {
  try {
    const response = await axiosInstance.get('/store/catalog');
    const data = response.data?.data ?? response.data;
    return { state: 'ready', items: Array.isArray(data) ? data : [] };
  } catch (error) {
    return { state: nonPaywalled(error) };
  }
};

/** Rupees for display. Money is held in paise precisely so this is the only place it becomes a decimal. */
export const formatPaise = (paise: number | null | undefined): string => {
  if (paise == null) return '—';
  return `₹${(paise / 100).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
};

/** Joins a field the API may return as a string or a list. */
export const asText = (v: string[] | string | undefined): string =>
  Array.isArray(v) ? v.join(', ') : (v ?? '');

// ── #07 live sessions ────────────────────────────────────────────────────────

export interface LiveSession {
  id: string;
  title: string;
  description?: string;
  teacherName?: string;
  packageId?: string;
  startsAt: string;
  durationMinutes?: number;
  status: 'SCHEDULED' | 'LIVE' | 'ENDED' | 'CANCELLED';
  capacity?: number | null;
  enrolledCount: number;
  placesLeft?: number | null;
  enrolled: boolean;
  /** Only ever populated by the server when `enrolled` is true. Never reconstruct it. */
  joinUrl?: string | null;
}

export type LiveResult =
  | { state: 'ready'; sessions: LiveSession[] }
  | { state: 'locked' | 'unauthorized' | 'error'; message?: string };

export const fetchLiveSessions = async (): Promise<LiveResult> => {
  try {
    const response = await axiosInstance.get('/live');
    const data = response.data?.data ?? response.data;
    return { state: 'ready', sessions: Array.isArray(data) ? data : [] };
  } catch (error) {
    return { state: classify(error) };
  }
};

/**
 * Enrolling is gated on the `live` SKU; withdrawing deliberately is not, because a
 * student whose entitlement lapsed must still be able to give their seat back.
 */
export const enrolInLive = async (id: string, studentId: string): Promise<'ok' | 'locked' | 'full' | 'error'> => {
  try {
    await axiosInstance.post(`/live/${id}/enrol`, null, { params: { studentId } });
    return 'ok';
  } catch (error) {
    const status = isAxios(error) ? error.response?.status : undefined;
    if (status === 402) return 'locked';
    if (status === 409) return 'full';
    return 'error';
  }
};

export const withdrawFromLive = async (id: string, studentId: string): Promise<boolean> => {
  try {
    await axiosInstance.delete(`/live/${id}/enrol`, { params: { studentId } });
    return true;
  } catch {
    return false;
  }
};
